import React, { useState } from 'react';
import { motion } from 'framer-motion';
//import { astrologyEndPoint } from '../../../Services/AllApi';
import { apiConnector } from '../../../Services/ApiConnector';
import { FaSpinner, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUser, FaPhone, FaTicketAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import HardcodedLocationSelector from '../../common/HardcodedLocationSelector';
import { useLanguage } from '../../../i18n/LanguageContext';
import { validateCoupon, getKundliApiConfig, formatApiParams } from '../../../Utils/couponUtils';

const KundliFeature = () => {
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedTime, setSelectedTime] = useState('10:50');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [kundliData, setKundliData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState({ isValid: true, message: '', type: 'empty' });

  //const { KUNDLI_API, KUNDLI_ADVANCED_API } = astrologyEndPoint;

  // Helper function to get user-friendly error messages for HTTP status codes
  const getStatusMessage = (status) => {
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please login and try again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'Service not found. Please try again later.';
      case 409:
        return 'Conflict error. Please check your data and try again.';
      case 422:
        return 'Invalid data provided. Please check your input and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Service temporarily unavailable. Please try again later.';
      case 503:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${status}. Please try again.`;
    }
  };

  // Helper function to extract readable text from API response
  const getReadableText = (data) => {
    // Debug logging to understand the data structure
    console.log('getReadableText received:', data, 'type:', typeof data);
    
    if (data === null || data === undefined) return 'N/A';
    
    // If it's already a string, return it
    if (typeof data === 'string') {
      return data.trim() || 'N/A';
    }
    
    // If it's an object, try to extract the text property
    if (typeof data === 'object') {
      // Check for common text properties
      if (data.text && typeof data.text === 'string') return data.text.trim();
      if (data.description && typeof data.description === 'string') return data.description.trim();
      if (data.message && typeof data.message === 'string') return data.message.trim();
      if (data.content && typeof data.content === 'string') return data.content.trim();
      
      // If it's an empty object or has no text properties, return N/A
      if (Object.keys(data).length === 0) return 'N/A';
      
      // If no text property found, return a fallback
      return 'Details available';
    }
    
    return 'N/A';
  };

  // Phone number validation function based on backend rules
  const validatePhoneNumber = (phoneInput) => {
    if (!phoneInput || phoneInput.trim() === '') {
      return {
        isValid: false,
        formatted: '',
        error: 'Phone number is required'
      };
    }

    // Remove all non-digit characters
    let digits = phoneInput.replace(/\D/g, '');
    
    // Handle different prefixes
    if (digits.startsWith('91') && digits.length === 12) {
      // Remove +91 or 91 prefix
      digits = digits.substring(2);
    } else if (digits.startsWith('0') && digits.length === 11) {
      // Remove leading 0
      digits = digits.substring(1);
    }

    // Check if exactly 10 digits after formatting
    if (digits.length !== 10) {
      return {
        isValid: false,
        formatted: digits,
        error: 'Phone number must be exactly 10 digits'
      };
    }

    // Check if starts with 6, 7, 8, or 9
    const firstDigit = digits.charAt(0);
    if (!['6', '7', '8', '9'].includes(firstDigit)) {
      return {
        isValid: false,
        formatted: digits,
        error: 'Phone number must start with 6, 7, 8, or 9'
      };
    }

    return {
      isValid: true,
      formatted: digits,
      error: ''
    };
  };

  // Handle phone input change with validation
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    setPhone(inputValue);
    
    const validation = validatePhoneNumber(inputValue);
    setPhoneError(validation.error);
  };

  // Handle location selection
  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  // Handle coupon code change with validation
  const handleCouponChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    
    // Validate coupon in real-time
    const validation = validateCoupon(value);
    
    // Update validation with translated messages
    if (validation.type === 'valid') {
      validation.message = t('validCouponMessage');
    } else if (validation.type === 'invalid') {
      validation.message = t('invalidCouponMessage');
    }
    
    setCouponValidation(validation);
  };

  const handleGetKundli = async () => {
    if (!name.trim()) {
      toast.error(t('pleaseEnterName'));
      return;
    }
    
    // Validate phone number
    const phoneValidation = validatePhoneNumber(phone);
    if (!phoneValidation.isValid) {
      toast.error(phoneValidation.error);
      setPhoneError(phoneValidation.error);
      return;
    }
    
    if (!selectedLocation || !selectedLocation.coordinates) {
      toast.error(t('pleaseSelectLocation'));
      return;
    }

    setLoading(true);
    setKundliData(null);

    try {
      // Format datetime
      const dateTime = new Date(`${selectedDate}T${selectedTime}:40+00:00`).toISOString();

      // Prepare base parameters with current language from context
      const baseParams = {
        ayanamsa: 1,
        coordinates: selectedLocation.coordinates,
        datetime: dateTime,
        name: name,
        phone: phoneValidation.formatted,
        language: language
      };
      
      // Get API configuration based on coupon validation
      const apiConfig = getKundliApiConfig(couponCode, baseParams);
      
      // Show appropriate message based on coupon validation
      if (couponCode.trim() && !couponValidation.isValid) {
        toast.error(couponValidation.message);
      } else if (couponCode.trim() && couponValidation.isValid) {
        toast.success(t('validCouponMessage'));
      }
      
      // Construct API URL
      const apiUrl = `${apiConfig.endpoint}?${formatApiParams(apiConfig.params)}`;
      
      // Debug logging
      console.log('API Configuration:', apiConfig);
      console.log('Language being sent:', language);
      console.log('Full API URL:', apiUrl);
      
      const response = await apiConnector(
        'GET',
        apiUrl,
        null,
        null
      );

      if (response?.data) {
        console.log('Full Kundli Response:', response.data);
        console.log('Yoga Details:', response.data.data?.yoga_details);
        console.log('API Used:', apiConfig.isAdvanced ? 'Advanced' : 'Standard');
        setKundliData(response.data);
        
        // Show success message based on API type used
        if (apiConfig.isAdvanced) {
          toast.success(t('advancedKundliGenerated'));
        } else {
          toast.success(t('kundliGeneratedSuccessfully'));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching kundli:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Handle different types of API errors with more specific messages
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // First, try to extract the actual error message from the response
        let errorMessage = null;
        
        // Check if the response data is a string (direct error message)
        if (typeof errorData === 'string') {
          errorMessage = errorData.trim();
        }
        // Check for specific error message fields
        else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (errorData.errors) {
          // Handle validation errors array
          if (Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.join(', ');
          } else {
            errorMessage = JSON.stringify(errorData.errors);
          }
        } else if (typeof errorData === 'object' && errorData !== null) {
          // If it's an object, try to find any string value that looks like an error message
          const stringValues = Object.values(errorData).filter(val => typeof val === 'string' && val.trim().length > 0);
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          }
        }
        
        // Display the error message if we found one
        if (errorMessage) {
          toast.error(errorMessage);
        } else {
          // If we have response data but no clear message, show the status
          const statusMessage = getStatusMessage(error.response.status);
          toast.error(statusMessage);
        }
      } else if (error.response?.status) {
        // Handle HTTP status codes without specific error data
        const statusMessage = getStatusMessage(error.response.status);
        toast.error(statusMessage);
      } else if (error.message) {
        // Handle network errors or other issues
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          toast.error('Network error. Please check your internet connection and try again.');
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error('Failed to generate kundli. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      id="kundli-generation"
      className="bg-white rounded-2xl shadow-lg p-8 mb-8"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      whileHover={{ scale: 1.02, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
    >
      <motion.h3 
        className="text-2xl font-bold text-orange-800 mb-6 text-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        >
          {t('kundliGeneration')}
        </motion.h3>

      {/* Input Form */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Name Input */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <FaUser className="inline mr-2" />
              {t('fullName')}
            </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {/* Phone Input */}
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <FaPhone className="inline mr-2" />
              {t('phoneNumber')}
            </label>
          <input
            type="tel"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="Enter your phone number (e.g., 7654217440, +917654217440, 917654217440, 07654217440)"
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
              phoneError ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {phoneError && (
            <p className="text-red-500 text-sm mt-1">{phoneError}</p>
          )}
            {phone && !phoneError && (
              <p className="text-green-600 text-sm mt-1">{t('validPhoneNumber')}</p>
            )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        {/* Date Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <FaCalendarAlt className="inline mr-2" />
            {t('selectDate')}
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {/* Time Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <FaClock className="inline mr-2" />
            {t('selectTime')}
          </label>
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
          />
        </div>

        {/* Location Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <FaMapMarkerAlt className="inline mr-2" />
            {t('selectBirthPlace')}
          </label>
          <HardcodedLocationSelector
            value={selectedLocation?.name || ''}
            onChange={handleLocationSelect}
            placeholder={t('searchForBirthPlace')}
            className="w-full"
          />
        </div>
      </div>

      {/* Coupon Code Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <FaTicketAlt className="inline mr-2" />
          {t('couponCodeOptional')}
        </label>
        <div className="relative">
          <input
            type="text"
            value={couponCode}
            onChange={handleCouponChange}
            placeholder={t('enterCouponCode')}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-orange-500 ${
              couponValidation.isValid 
                ? couponValidation.type === 'valid' 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-gray-300'
                : 'border-red-500 bg-red-50'
            }`}
          />
          {couponCode && (
            <div className={`mt-2 p-2 rounded-md text-sm ${
              couponValidation.isValid 
                ? couponValidation.type === 'valid'
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-gray-100 text-gray-600'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}>
              {couponValidation.message}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t('couponValidationMessage')}
        </p>
      </div>

      {/* Generate Button */}
      <motion.div 
        className="text-center mb-6"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.button
          onClick={handleGetKundli}
          disabled={loading}
          className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50"
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.95 }}
        >
            {loading ? (
              <>
                <FaSpinner className="animate-spin inline mr-2" /> {t('generatingKundli')}
              </>
            ) : (
              t('generateKundli')
            )}
        </motion.button>
      </motion.div>

      {/* Results */}
      {kundliData?.data && (
        <motion.div 
          className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border-2 border-orange-200"
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-xl font-bold text-orange-800">
              {t('kundliDetails')}
            </h4>
            <div className="text-right">
              <span className="text-sm text-gray-600 block">
                {selectedLocation?.name || t('locationNotSelected')} • {new Date(selectedDate).toLocaleDateString()} • {selectedTime}
              </span>
              {couponCode && couponValidation.isValid && couponValidation.type === 'valid' && (
                <span className="text-xs text-green-600 font-semibold">
                  {t('advancedFeaturesEnabled')}
                </span>
              )}
            </div>
          </div>


          <div className="space-y-8">
            {/* Nakshatra Details Section */}
            {kundliData.data.nakshatra_details && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('nakshatraDetails')}</h3>

                {/* Grid layout for nakshatra details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Nakshatra Box */}
                  <div className="rounded-lg p-4 border border-blue-200 bg-blue-50">
                    <h4 className="text-base font-semibold text-blue-900 mb-3">{t('nakshatra')}</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-blue-800 font-medium">{t('name') || 'Name'}</dt>
                      <dd className="text-blue-900 text-right sm:text-left">{kundliData.data.nakshatra_details.nakshatra?.name || 'N/A'}</dd>
                      <dt className="text-blue-800 font-medium">{t('lord')}</dt>
                      <dd className="text-blue-900 text-right sm:text-left">{kundliData.data.nakshatra_details.nakshatra?.lord?.name || 'N/A'}</dd>
                      <dt className="text-blue-800 font-medium">{t('vedicName')}</dt>
                      <dd className="text-blue-900 text-right sm:text-left">{kundliData.data.nakshatra_details.nakshatra?.lord?.vedic_name || 'N/A'}</dd>
                      <dt className="text-blue-800 font-medium">{t('pada')}</dt>
                      <dd className="text-blue-900 text-right sm:text-left">{kundliData.data.nakshatra_details.nakshatra?.pada || 'N/A'}</dd>
                    </dl>
                  </div>

                  {/* Chandra Rasi Box */}
                  <div className="rounded-lg p-4 border border-purple-200 bg-purple-50">
                    <h4 className="text-base font-semibold text-purple-900 mb-3">{t('chandraRasi')}</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-purple-800 font-medium">{t('name') || 'Name'}</dt>
                      <dd className="text-purple-900 text-right sm:text-left">{kundliData.data.nakshatra_details.chandra_rasi?.name || 'N/A'}</dd>
                      <dt className="text-purple-800 font-medium">{t('lord')}</dt>
                      <dd className="text-purple-900 text-right sm:text-left">{kundliData.data.nakshatra_details.chandra_rasi?.lord?.name || 'N/A'}</dd>
                      <dt className="text-purple-800 font-medium">{t('vedicName')}</dt>
                      <dd className="text-purple-900 text-right sm:text-left">{kundliData.data.nakshatra_details.chandra_rasi?.lord?.vedic_name || 'N/A'}</dd>
                    </dl>
                  </div>

                  {/* Soorya Rasi Box */}
                  <div className="rounded-lg p-4 border border-amber-200 bg-amber-50">
                    <h4 className="text-base font-semibold text-amber-900 mb-3">{t('sooryaRasi')}</h4>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <dt className="text-amber-800 font-medium">{t('name') || 'Name'}</dt>
                      <dd className="text-amber-900 text-right sm:text-left">{kundliData.data.nakshatra_details.soorya_rasi?.name || 'N/A'}</dd>
                      <dt className="text-amber-800 font-medium">{t('lord')}</dt>
                      <dd className="text-amber-900 text-right sm:text-left">{kundliData.data.nakshatra_details.soorya_rasi?.lord?.name || 'N/A'}</dd>
                      <dt className="text-amber-800 font-medium">{t('vedicName')}</dt>
                      <dd className="text-amber-900 text-right sm:text-left">{kundliData.data.nakshatra_details.soorya_rasi?.lord?.vedic_name || 'N/A'}</dd>
                    </dl>
                  </div>
                </div>

                {/* Zodiac and Additional Info in separate section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                  {/* Zodiac */}
                  <div className="rounded-lg p-4 border border-gray-200 bg-gray-50">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">{t('zodiac')}:</span>
                      <span className="text-gray-900 font-medium">{kundliData.data.nakshatra_details.zodiac?.name || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Additional Info condensed */}
                  {kundliData.data.nakshatra_details?.additional_info && (
                    <div className="rounded-lg p-4 border border-emerald-200 bg-emerald-50">
                      <h4 className="text-base font-semibold text-emerald-900 mb-3">{t('additionalInfo')}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="font-medium text-emerald-800">{t('deity')}:</span> <span className="text-emerald-900">{kundliData.data.nakshatra_details.additional_info.deity || 'N/A'}</span></div>
                        <div><span className="font-medium text-emerald-800">{t('ganam')}:</span> <span className="text-emerald-900">{kundliData.data.nakshatra_details.additional_info.ganam || 'N/A'}</span></div>
                        <div><span className="font-medium text-emerald-800">{t('symbol')}:</span> <span className="text-emerald-900">{kundliData.data.nakshatra_details.additional_info.symbol || 'N/A'}</span></div>
                        <div><span className="font-medium text-emerald-800">{t('color')}:</span> <span className="text-emerald-900">{kundliData.data.nakshatra_details.additional_info.color || 'N/A'}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mangal Dosha Section */}
            {kundliData.data.mangal_dosha && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('mangalDosha')}</h3>

                {/* Status + Type */}
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                    kundliData.data.mangal_dosha.has_dosha 
                      ? 'bg-red-100 text-red-800 border border-red-200' 
                      : 'bg-green-100 text-green-800 border border-green-200'
                  }`}>
                    {kundliData.data.mangal_dosha.has_dosha ? t('hasMangalDosha') : t('noMangalDosha')}
                  </div>
                  {kundliData.data.mangal_dosha.type && (
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                      {t('doshaType') || 'Type'}: {kundliData.data.mangal_dosha.type}
                    </div>
                  )}
                </div>

                {/* Description */}
                {kundliData.data.mangal_dosha.description && (
                  <p className="text-sm text-gray-700 mb-4 p-4 bg-gray-50 rounded-lg">
                    {getReadableText(kundliData.data.mangal_dosha.description)}
                  </p>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Exceptions */}
                  {Array.isArray(kundliData.data.mangal_dosha.exceptions) && kundliData.data.mangal_dosha.exceptions.length > 0 && (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-2">{t('exceptions') || 'Exceptions'}</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-yellow-900">
                        {kundliData.data.mangal_dosha.exceptions.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Remedies */}
                  {Array.isArray(kundliData.data.mangal_dosha.remedies) && kundliData.data.mangal_dosha.remedies.length > 0 && (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <h4 className="text-sm font-semibold text-green-900 mb-2">{t('remedies') || 'Remedies'}</h4>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-green-900">
                        {kundliData.data.mangal_dosha.remedies.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Yoga Details Section */}
            {Array.isArray(kundliData.data.yoga_details) && kundliData.data.yoga_details.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('yogas')}</h3>
                
                <div className="space-y-6">
                  {kundliData.data.yoga_details.map((yoga, index) => {
                    // Try multiple ways to get the description text
                    const getYogaDescription = (yogaData) => {
                      if (yogaData.description) {
                        const desc = getReadableText(yogaData.description);
                        if (desc !== 'N/A' && desc !== 'Details available') {
                          return desc;
                        }
                      }
                      if (yogaData.text) {
                        const text = getReadableText(yogaData.text);
                        if (text !== 'N/A' && text !== 'Details available') {
                          return text;
                        }
                      }
                      if (yogaData.content) {
                        const content = getReadableText(yogaData.content);
                        if (content !== 'N/A' && content !== 'Details available') {
                          return content;
                        }
                      }
                      return 'Details not available';
                    };
                    
                    return (
                      <div key={index} className="border border-indigo-200 rounded-lg p-4 bg-indigo-50">
                        <h4 className="text-lg font-bold text-indigo-900 mb-3">{yoga.name}</h4>
                        {getYogaDescription(yoga) && (
                          <p className="text-sm text-indigo-800 mb-4 whitespace-pre-wrap bg-white p-3 rounded">{getYogaDescription(yoga)}</p>
                        )}

                        {/* Yoga list */}
                        {Array.isArray(yoga.yoga_list) && yoga.yoga_list.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {yoga.yoga_list.map((item, idx) => (
                              <div key={idx} className="rounded-lg border border-white p-3 bg-white shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-gray-800">{item.name}</span>
                                  <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                                    item.has_yoga ? 'bg-green-100 text-green-800 border-green-200' : 'bg-red-100 text-red-700 border-red-200'
                                  }`}>
                                    {item.has_yoga ? (t('hasYoga') || 'Present') : (t('noYoga') || 'Not Present')}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dasha Periods Section */}
            {Array.isArray(kundliData.data.dasha_periods) && kundliData.data.dasha_periods.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('dashaPeriods')}</h3>
                
                <div className="space-y-6">
                  {kundliData.data.dasha_periods.map((mahadasha, mIdx) => (
                    <div key={mIdx} className="border border-violet-200 rounded-lg p-4 bg-violet-50">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-bold text-violet-900">{mahadasha.name}</h4>
                        <div className="text-sm text-violet-700 bg-white px-3 py-1 rounded-full">
                          {new Date(mahadasha.start).toLocaleDateString()} → {new Date(mahadasha.end).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Antardasha list */}
                      {Array.isArray(mahadasha.antardasha) && mahadasha.antardasha.length > 0 && (
                        <div>
                          <div className="text-sm font-semibold text-violet-800 mb-3">{t('antardasha')}</div>
                          <div className="space-y-3">
                            {mahadasha.antardasha.map((antar, aIdx) => (
                              <div key={aIdx} className="rounded-lg border border-white p-3 bg-white shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-gray-800">{antar.name}</span>
                                  <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                    {new Date(antar.start).toLocaleDateString()} → {new Date(antar.end).toLocaleDateString()}
                                  </span>
                                </div>
                                
                                {/* Pratyantardasha list */}
                                {Array.isArray(antar.pratyantardasha) && antar.pratyantardasha.length > 0 && (
                                  <div className="mt-3">
                                    <div className="text-xs font-semibold text-gray-700 mb-2">{t('pratyantardasha')}</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                      {antar.pratyantardasha.map((pratya, pIdx) => (
                                        <div key={pIdx} className="rounded border bg-gray-50 p-2 text-xs">
                                          <div className="font-medium text-gray-800 mb-1">{pratya.name}</div>
                                          <div className="text-gray-600">{new Date(pratya.start).toLocaleDateString()} → {new Date(pratya.end).toLocaleDateString()}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dasha Balance Section */}
            {kundliData.data.dasha_balance && (
              <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">{t('dashaBalance') || 'Current Dasha Balance'}</h3>
                
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Lord Information */}
                    {kundliData.data.dasha_balance.lord && (
                      <div className="space-y-3">
                        <h4 className="text-lg font-bold text-emerald-900 flex items-center gap-2">
                          🔮 {t('currentDashaLord') || 'Current Dasha Lord'}
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-emerald-700">{t('name') || 'Name'}:</span>
                            <span className="text-emerald-900 font-semibold">{kundliData.data.dasha_balance.lord.name || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-emerald-700">{t('vedicName') || 'Vedic Name'}:</span>
                            <span className="text-emerald-900">{kundliData.data.dasha_balance.lord.vedic_name || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Duration Information */}
                    <div className="space-y-3">
                      <h4 className="text-lg font-bold text-teal-900 flex items-center gap-2">
                        ⏰ {t('remainingDuration') || 'Remaining Duration'}
                      </h4>
                      <div className="bg-white rounded-lg p-4 border border-teal-200">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-teal-900 mb-2">
                            {kundliData.data.dasha_balance.duration || 'N/A'}
                          </div>
                          <div className="text-sm text-teal-700">
                            {kundliData.data.dasha_balance.description || 'Duration details not available'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Balance Information */}
                  {(kundliData.data.dasha_balance.years || kundliData.data.dasha_balance.months || kundliData.data.dasha_balance.days) && (
                    <div className="mt-6 bg-white rounded-lg p-4 border border-emerald-200">
                      <div className="text-center">
                        <h5 className="text-md font-semibold text-gray-800 mb-3">{t('detailedBalance') || '⏳ Detailed Balance'}</h5>
                        <div className="flex justify-center items-center gap-6 text-sm">
                          {kundliData.data.dasha_balance.years && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-emerald-800">{kundliData.data.dasha_balance.years}</div>
                              <div className="text-emerald-600">{t('years') || 'Years'}</div>
                            </div>
                          )}
                          {kundliData.data.dasha_balance.months && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-teal-800">{kundliData.data.dasha_balance.months}</div>
                              <div className="text-teal-600">{t('months') || 'Months'}</div>
                            </div>
                          )}
                          {kundliData.data.dasha_balance.days && (
                            <div className="text-center">
                              <div className="text-xl font-bold text-cyan-800">{kundliData.data.dasha_balance.days}</div>
                              <div className="text-cyan-600">{t('days') || 'Days'}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

    </motion.div>
  );
};

export default KundliFeature;
